import {
  DataGridPremium,
  GridColDef,
  GridListViewColDef,
  GridRenderCellParams,
  GridToolbarProps,
} from '@mui/x-data-grid-premium';
import { DEBOUNCE_MS } from '@/constants/timing';
import { defaultToolbarExportSlotProps } from '@/components/table/toolbarSlotProps';
import dayjs from '@/config/dayjs';
import React, { useCallback, useMemo } from 'react';
import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';
import { useGetDocumentsHistoryQuery } from '@/store/documentsApi';
import type { DocumentHistoryRecord } from '@/store/documentsApi';
import { useLocalization } from '@/hooks/useLocalization';
import {
  Box,
  Button,
  IconButton,
  Stack,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  useMediaQuery,
} from '@mui/material';
import CustomToolbarForNative from '@/components/CustomToolbarForNative';
import XsPreferencesDrawerToolbar from '@/components/table/XsPreferencesDrawerToolbar';
import { mobileWidth } from '@/constants';
import CloseIcon from '@mui/icons-material/Close';
import { Capacitor } from '@capacitor/core';
import { GridContainer } from '@/components/grids/GridContainer';

interface DocumentOpenEntry {
  OpenDate?: string | number | Date | null;
  OpenedBy?: string | null;
}

interface DocumentsHistoryModalProps {
  open: boolean;
  onClose: () => void;
  investorId?: number; // when present and role=advisor, hits /advisor/investors/{id}/documentsHistory
}

const desktopPaperSx = {
  m: 0,
  width: '100%',
  maxWidth: '100%',
  height: '100%',
  maxHeight: '100%',
  border: '1px solid #ccc',
  borderRadius: 1,
};

const DocumentsHistoryModal: React.FC<DocumentsHistoryModalProps> = ({
  open,
  onClose,
  investorId,
}) => {
  const isAuthLoaded = useAuthIsLoaded();
  const { t } = useLocalization();
  const isXs = useMediaQuery(mobileWidth);
  const isListView = isXs;
  const isNative = Capacitor.getPlatform() !== 'web';

  // Lazy fetch: only when modal is opened
  const { data, isLoading, error } = useGetDocumentsHistoryQuery(
    typeof investorId === 'number' ? { InvestorId: investorId } : undefined,
    { skip: !open || !isAuthLoaded },
  );

  interface DocumentHistoryRow {
    id: number | string;
    Label: string;
    Parents?: string;
    NumberOfOpens: number;
    LastOpened: string | null;
    UploadDate: string | null;
    Opens: DocumentOpenEntry[];
  }

  type HistoryExtras = {
    Label?: string;
    Parents?: string;
    NumberOfOpens?: number | string;
    Opens?: DocumentOpenEntry[];
    LastReadDate?: string;
    LastOpenedDate?: string;
    LastOpenedOn?: string;
    Uploaded?: string;
    DateUploaded?: string;
  };

  const rows = useMemo<DocumentHistoryRow[]>(() => {
    // The endpoint can return empty entries, so they are skipped here.
    const records: ((DocumentHistoryRecord & HistoryExtras) | null)[] =
      data?.Records ?? [];
    return records
      .filter((r): r is DocumentHistoryRecord & HistoryExtras => r !== null)
      .map((r, idx) => ({
        id: r.VaultFileId ?? idx,
        Label: r.Label ?? r.Name ?? '',
        Parents: r.Parents ?? '',
        Opens: Array.isArray(r.Opens)
          ? r.Opens.map((o) => {
              const entry = o as Partial<DocumentOpenEntry>;
              return {
                OpenDate: entry.OpenDate ?? null,
                OpenedBy: entry.OpenedBy ?? null,
              };
            })
          : [],
        NumberOfOpens:
          typeof r.NumberOfOpens === 'number'
            ? r.NumberOfOpens
            : typeof r.NumberOfOpens === 'string'
              ? Number.isFinite(Number(r.NumberOfOpens))
                ? Number(r.NumberOfOpens)
                : Array.isArray(r.Opens)
                  ? r.Opens.length
                  : 0
              : Array.isArray(r.Opens)
                ? r.Opens.length
                : 0,
        LastOpened:
          r.LastOpened ??
          r.LastReadDate ??
          r.LastOpenedDate ??
          r.LastOpenedOn ??
          null,
        UploadDate: r.UploadDate ?? r.Uploaded ?? r.DateUploaded ?? null,
      }));
  }, [data]);

  const [opensDialogState, setOpensDialogState] = React.useState<{
    open: boolean;
    fileLabel: string;
    parents?: string;
    opens: DocumentOpenEntry[];
  }>({ open: false, fileLabel: '', parents: undefined, opens: [] });

  const handleShowOpens = useCallback((row: DocumentHistoryRow) => {
    setOpensDialogState({
      open: true,
      fileLabel: row.Label,
      parents: row.Parents,
      opens: row.Opens,
    });
  }, []);

  const handleCloseOpens = () => {
    setOpensDialogState((s) => ({ ...s, open: false }));
  };

  const formatDate = useCallback(
    (value: string | number | Date | null | undefined): string => {
      if (!value) return '';
      // Handle '/Date(1716422400000)/' style values
      if (typeof value === 'string') {
        const match = value.match(/\/Date\((\d+)\)\//);
        if (match) {
          const ts = Number(match[1]);
          return dayjs(ts).isValid() ? dayjs(ts).format('MM/DD/YYYY') : value;
        }
        const m = dayjs(value);
        if (m.isValid()) return m.format('MM/DD/YYYY');
        // Try constructing a Date if string isn't directly parseable by dayjs
        const m2 = dayjs(new Date(value));
        return m2.isValid() ? m2.format('MM/DD/YYYY') : value;
      }
      if (typeof value === 'number') {
        return dayjs(value).isValid()
          ? dayjs(value).format('MM/DD/YYYY')
          : String(value);
      }
      if (value instanceof Date) {
        return dayjs(value).format('MM/DD/YYYY');
      }
      return String(value);
    },
    [],
  );

  const columns: GridColDef<DocumentHistoryRow>[] = useMemo(
    () => [
      {
        field: 'Label',
        headerName: t('documents.grid.file'),
        flex: 1,
        sortable: true,
        cellClassName: 'va-center',
        renderCell: (
          params: GridRenderCellParams<DocumentHistoryRow, string>,
        ) => {
          const label = params.row.Label;
          const parents = params.row.Parents;
          return (
            <Stack
              direction='row'
              gap={1}
              alignItems='center'
              sx={{ overflow: 'hidden' }}
            >
              <Box
                component='span'
                sx={{
                  display: 'inline-flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }}
              >
                {parents ? (
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{
                      lineHeight: 1.2,
                      display: 'block',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                    }}
                  >
                    {parents} /{/* show as folder path */}
                  </Typography>
                ) : null}
                <Typography
                  variant='body2'
                  sx={{
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                  }}
                >
                  {label}
                </Typography>
              </Box>
            </Stack>
          );
        },
      },
      {
        field: 'NumberOfOpens',
        headerName: t('documents.grid.numberOfOpens'),
        width: 120,
        type: 'number',
        sortable: true,
        cellClassName: 'va-center',
        renderCell: (
          params: GridRenderCellParams<DocumentHistoryRow, number>,
        ) => {
          const count = params.row.NumberOfOpens;
          const disabled = count <= 0;
          return (
            <Button
              variant='text'
              size='small'
              onClick={() => {
                handleShowOpens(params.row);
              }}
              disabled={disabled}
              sx={{
                color: 'primary.main',
                fontWeight: '700 !important',
                textTransform: 'none',
                minWidth: 0,
                p: 0.5,
              }}
            >
              {count}
            </Button>
          );
        },
      },
      {
        field: 'LastOpened',
        headerName: t('documents.grid.lastOpened'),
        width: 160,
        sortable: true,
        renderCell: (
          params: GridRenderCellParams<DocumentHistoryRow, string | null>,
        ) => formatDate(params.value),
        filterable: true,
      },
      {
        field: 'UploadDate',
        headerName: t('documents.grid.uploaded'),
        width: 160,
        sortable: true,
        renderCell: (
          params: GridRenderCellParams<DocumentHistoryRow, string | null>,
        ) => formatDate(params.value),
        filterable: true,
      },
    ],
    [formatDate, handleShowOpens, t],
  );

  const listViewColDef: GridListViewColDef<DocumentHistoryRow> = useMemo(
    () => ({
      field: 'listColumn',
      renderCell: (params: GridRenderCellParams<DocumentHistoryRow>) => {
        const row = params.row;
        const fileName = row.Label;
        const lastOpened = formatDate(row.LastOpened);
        const uploaded = formatDate(row.UploadDate);
        const count = row.NumberOfOpens;
        const opensDisabled = count <= 0;

        return (
          <Box
            sx={{
              width: '100%',
              px: 0,
              pr: 1,
              py: 1.5,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.75,
              minWidth: 0,
            }}
          >
            {/* Line 1 */}
            <Typography
              variant='body1'
              sx={{
                fontWeight: '600 !important',
                color: 'text.primary',
                minWidth: 0,
                whiteSpace: 'normal',
                overflow: 'visible',
                textOverflow: 'unset',
                overflowWrap: 'anywhere',
                wordBreak: 'break-word',
              }}
            >
              {fileName}
            </Typography>

            {/* Line 2 */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                gap: 1,
                minWidth: 0,
              }}
            >
              <Typography
                variant='body2'
                sx={{ color: 'text.secondary', fontWeight: '400 !important' }}
              >
                {t('documents.grid.lastOpened')}
              </Typography>
              <Typography
                variant='body2'
                sx={{
                  color: 'text.secondary',
                  fontWeight: '400 !important',
                  textAlign: 'right',
                  minWidth: 0,
                }}
              >
                {lastOpened}
              </Typography>
            </Box>

            {/* Line 3 */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                gap: 1,
                minWidth: 0,
              }}
            >
              <Typography
                variant='body2'
                sx={{ color: 'text.secondary', fontWeight: '400 !important' }}
              >
                {t('documents.grid.uploaded')}
              </Typography>
              <Typography
                variant='body2'
                sx={{
                  color: 'text.secondary',
                  fontWeight: '400 !important',
                  textAlign: 'right',
                  minWidth: 0,
                }}
              >
                {uploaded}
              </Typography>
            </Box>

            {/* Line 4 */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                gap: 1,
                minWidth: 0,
              }}
            >
              <Typography
                variant='body2'
                sx={{ color: 'text.secondary', fontWeight: '400 !important' }}
              >
                {t('documents.grid.numberOfOpens')}
              </Typography>
              <Button
                variant='text'
                size='small'
                onClick={(event) => {
                  event.stopPropagation();
                  handleShowOpens(row);
                }}
                disabled={opensDisabled}
                sx={{
                  color: 'primary.main',
                  textTransform: 'none',
                  fontWeight: '700 !important',
                  minWidth: 0,
                  p: 0,
                  justifyContent: 'flex-end',
                }}
              >
                {count}
              </Button>
            </Box>
          </Box>
        );
      },
    }),
    [formatDate, handleShowOpens, t],
  );

  const listViewSx = useMemo(
    () => ({
      '& .MuiDataGrid-virtualScrollerRenderZone': {
        width: '100% !important',
      },
      '& .MuiDataGrid-columnHeaders': { display: 'none' },
      '& .MuiDataGrid-cell': {
        px: 0,
        py: 0,
        width: '100%',
        minWidth: '100%',
        maxWidth: '100%',
      },
      '& .MuiDataGrid-row': {
        width: '100% !important',
        minWidth: '100% !important',
        maxWidth: '100% !important',
      },
      '& .MuiDataGrid-cellContent': {
        width: '100%',
        minWidth: 0,
      },
    }),
    [],
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        aria-labelledby='documents-history-modal'
        fullScreen={isXs}
        fullWidth
        maxWidth={false}
        slotProps={{
          paper: { sx: isXs ? undefined : desktopPaperSx },
        }}
      >
        <Box
          sx={{
            position: 'relative',
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            p: isXs ? 2 : 4,
          }}
        >
          <IconButton
            aria-label={t('close')}
            onClick={onClose}
            size='small'
            sx={{
              position: 'absolute',
              top: 20,
              right: 20,
            }}
          >
            <CloseIcon fontSize='small' />
          </IconButton>
          <Typography
            id='documents-history-modal'
            variant='h6'
            sx={{ mb: 2 }}
          >
            {t('documents.historyModal.title')}
          </Typography>
          {error ? (
            <Typography color='error'>
              {t('documents.historyModal.loadError')}
            </Typography>
          ) : (
            <GridContainer
              isWidget={false}
              size='flyout'
            >
              <DataGridPremium
                rows={rows}
                columns={columns}
                loading={isLoading}
                disableRowSelectionOnClick
                showToolbar={false}
                disablePivoting
                {...(isListView ? { listView: true } : {})}
                {...(isListView ? { listViewColumn: listViewColDef } : {})}
                {...(isListView ? { getRowHeight: () => 'auto' as const } : {})}
                sx={{
                  '& .MuiDataGrid-cell.va-center': {
                    display: 'flex',
                    alignItems: 'center',
                  },
                  ...(isListView ? listViewSx : {}),
                  // Keep virtualization smooth, and reserve space for the vertical scrollbar
                  // so it doesn't overlay right-aligned content (e.g. "# of opens" link).
                  '& .MuiDataGrid-virtualScroller': {
                    scrollbarGutter: 'stable',
                  },
                  '& .MuiDataGrid-cellContent': {
                    pr: 2,
                  },
                }}
                slotProps={{
                  toolbar: {
                    ...defaultToolbarExportSlotProps,
                    showQuickFilter: true,
                    quickFilterProps: { debounceMs: DEBOUNCE_MS.DEFAULT },
                  },
                }}
                slots={{
                  ...(isNative
                    ? {
                        toolbar: (props: GridToolbarProps) => (
                          <CustomToolbarForNative {...props} />
                        ),
                      }
                    : isListView
                      ? { toolbar: XsPreferencesDrawerToolbar }
                      : {}),
                }}
                initialState={{
                  sorting: {
                    sortModel: [{ field: 'LastOpened', sort: 'desc' }],
                  },
                  filter: { filterModel: { items: [] } },
                }}
              />
            </GridContainer>
          )}
        </Box>
      </Dialog>
      <OpensList
        open={opensDialogState.open}
        onClose={handleCloseOpens}
        fileLabel={opensDialogState.fileLabel}
        parents={opensDialogState.parents}
        opens={opensDialogState.opens}
        isMobile={isXs}
        t={t}
      />
    </>
  );
};

const OpensList: React.FC<{
  open: boolean;
  onClose: () => void;
  fileLabel: string;
  parents?: string;
  opens: DocumentOpenEntry[];
  isMobile: boolean;
  t: ReturnType<typeof useLocalization>['t'];
}> = ({ open, onClose, fileLabel, parents, opens, isMobile, t }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth='sm'
      fullWidth
    >
      <DialogTitle sx={{ pb: 1, position: 'relative', pr: 5 }}>
        <IconButton
          aria-label={t('close')}
          onClick={onClose}
          size='small'
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
          }}
        >
          <CloseIcon fontSize='small' />
        </IconButton>
        <Stack>
          {parents ? (
            <Typography
              variant='caption'
              color='text.secondary'
              sx={{ lineHeight: 1.2 }}
            >
              {parents}
            </Typography>
          ) : null}
          <Typography
            variant='subtitle1'
            sx={{ fontWeight: 700 }}
          >
            {fileLabel}
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack
          direction='row'
          alignItems='center'
          gap={1}
          sx={{ mb: 1 }}
        >
          <Chip
            size='small'
            label={t('documents.historyModal.opens.total', {
              count: opens.length,
            })}
          />
        </Stack>
        {opens.length > 0 ? (
          <List dense>
            {opens.map((o, idx) => {
              const primary = dayjs(o.OpenDate).isValid()
                ? dayjs(o.OpenDate).format('M/D/YYYY h:mm a')
                : String(o.OpenDate ?? '');
              const secondary = o.OpenedBy
                ? t('documents.historyModal.opens.by', { name: o.OpenedBy })
                : undefined;
              return (
                <React.Fragment key={idx}>
                  <ListItem>
                    <ListItemText
                      primary={primary}
                      secondary={secondary}
                    />
                  </ListItem>
                  {idx < opens.length - 1 ? <Divider component='li' /> : null}
                </React.Fragment>
              );
            })}
          </List>
        ) : (
          <Typography
            variant='body2'
            color='text.secondary'
          >
            {t('documents.historyModal.opens.empty')}
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DocumentsHistoryModal;
